// MembershipCard — renders an audience, file or ZIP file membership leaf in the
// expression tree. Counterpart to VariableCard, but the payload is just an
// id + display name (no input controls).
//
// Drag-drop wiring mirrors VariableCard:
//   • drag source: EXPRESSION_LEAF carrying { nodeId, zoneType }
//   • drop target: accepts VARIABLE_ATTRIBUTE / MY_DATA_ATTRIBUTE / EXPRESSION_LEAF
//     - VARIABLE_ATTRIBUTE → ADD_ATTRIBUTE_AND_WRAP
//     - MY_DATA_ATTRIBUTE   → ADD_MY_DATA_AND_WRAP
//     - EXPRESSION_LEAF     → WRAP_IN_GROUP
//
// PR 2c. ATAD-146: the wiring lives in useMembershipCardDnd so ModelCard (a
// membership card with a score range) behaves exactly the same.

import { useDrag, useDrop } from 'react-dnd';
import { canAddAttribute } from '../../helpers/dataDictionary';
import { getZoneVariables, membershipLeafId } from '../../helpers/expressionTree';
import {
  ADD_ATTRIBUTE_AND_WRAP,
  ADD_MY_DATA_AND_WRAP,
  REMOVE_MY_DATA_RESOURCE,
  TYPES,
  WRAP_IN_GROUP,
} from '../../reducers/builderReducer';
import type {
  MembershipLeaf,
  MembershipResourceType,
} from '../../types/dataDictionary';

const { VARIABLE_ATTRIBUTE, MY_DATA_ATTRIBUTE, EXPRESSION_LEAF } = TYPES;

const DEFAULT_GROUP_OP = 'or' as const;

export type MembershipCardProps = {
  leaf: MembershipLeaf;
  zoneType: 'include' | 'exclude';
  builderState: any;
  builderDispatch: (action: { type: string; payload?: any }) => void;
};

type LeafDragItem = { nodeId: string; zoneType: 'include' | 'exclude' };
type DictDragItem = { id: string; name: string; allowedZoneTypes?: string };
type MyDataDragItem = {
  id: string;
  name: string;
  resourceType: MembershipResourceType;
};

const LABELS: Record<MembershipResourceType, string> = {
  audience: 'Purchased Audience',
  file: 'Uploaded File',
  zip: 'ZIP Code File',
  model: 'Model File',
};

/** Card heading, e.g. "ZIP Code File · 1,234 ZIPs" once the count is known. */
export function membershipCardLabel(leaf: MembershipLeaf): string {
  const label = LABELS[leaf.kind];
  if (leaf.kind === 'zip' && typeof leaf.zipCount === 'number') {
    const count = leaf.zipCount.toLocaleString('en-US');
    return `${label} · ${count} ZIP${leaf.zipCount === 1 ? '' : 's'}`;
  }
  return label;
}

/**
 * Drag-drop wiring for a membership card (see the header comment). Returns
 * the ref callback for the card's root element, plus the two states the card
 * styles itself by.
 */
export function useMembershipCardDnd({
  leaf,
  zoneType,
  builderState,
  builderDispatch,
}: MembershipCardProps) {
  const resourceType: MembershipResourceType = leaf.kind;
  const resourceId = membershipLeafId(leaf);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: EXPRESSION_LEAF,
      item: { nodeId: leaf.nodeId, zoneType } as LeafDragItem,
      collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    }),
    [leaf.nodeId, zoneType],
  );

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: [VARIABLE_ATTRIBUTE, MY_DATA_ATTRIBUTE, EXPRESSION_LEAF],
      drop: (
        item: DictDragItem | MyDataDragItem | LeafDragItem,
        monitor,
      ) => {
        const dragType = monitor.getItemType();
        if (dragType === VARIABLE_ATTRIBUTE) {
          const dict = item as DictDragItem;
          builderDispatch({
            type: ADD_ATTRIBUTE_AND_WRAP,
            payload: {
              id: dict.id,
              targetNodeId: leaf.nodeId,
              op: DEFAULT_GROUP_OP,
              zoneType,
            },
          });
          return;
        }
        if (dragType === MY_DATA_ATTRIBUTE) {
          const md = item as MyDataDragItem;
          builderDispatch({
            type: ADD_MY_DATA_AND_WRAP,
            payload: {
              id: md.id,
              name: md.name,
              type: md.resourceType,
              targetNodeId: leaf.nodeId,
              op: DEFAULT_GROUP_OP,
              zoneType,
            },
          });
          return;
        }
        if (dragType === EXPRESSION_LEAF) {
          const src = item as LeafDragItem;
          builderDispatch({
            type: WRAP_IN_GROUP,
            payload: {
              sourceNodeId: src.nodeId,
              targetNodeId: leaf.nodeId,
              op: DEFAULT_GROUP_OP,
              zoneType,
            },
          });
        }
      },
      canDrop: (item, monitor) => {
        const dragType = monitor.getItemType();
        if (dragType === VARIABLE_ATTRIBUTE) {
          const dict = item as DictDragItem;
          if (
            dict.allowedZoneTypes &&
            !dict.allowedZoneTypes.includes(zoneType)
          ) {
            return false;
          }
          const sameZone = getZoneVariables(builderState, zoneType);
          const otherZone = getZoneVariables(
            builderState,
            zoneType === 'include' ? 'exclude' : 'include',
          );
          return (
            canAddAttribute(sameZone, dict.id) &&
            canAddAttribute(otherZone, dict.id)
          );
        }
        if (dragType === MY_DATA_ATTRIBUTE) {
          // Same-id-same-type can't be added twice (it's already this card).
          const md = item as MyDataDragItem;
          return !(md.id === resourceId && md.resourceType === resourceType);
        }
        if (dragType === EXPRESSION_LEAF) {
          const src = item as LeafDragItem;
          if (src.zoneType !== zoneType) return false;
          if (src.nodeId === leaf.nodeId) return false;
          return true;
        }
        return false;
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver({ shallow: true }),
        canDrop: !!monitor.canDrop(),
      }),
    }),
    [leaf.nodeId, zoneType, builderState, resourceId, resourceType],
  );

  return {
    connect: (el: HTMLElement | null) => {
      drag(el);
      drop(el);
    },
    isDragging,
    isHighlighted: isOver && canDrop,
  };
}

export default function MembershipCard({
  leaf,
  zoneType,
  builderState,
  builderDispatch,
}: MembershipCardProps) {
  const resourceType: MembershipResourceType = leaf.kind;
  const resourceId = membershipLeafId(leaf);
  const label = membershipCardLabel(leaf);
  const { connect, isDragging, isHighlighted } = useMembershipCardDnd({
    leaf,
    zoneType,
    builderState,
    builderDispatch,
  });

  const handleRemove = () =>
    builderDispatch({
      type: REMOVE_MY_DATA_RESOURCE,
      payload: { id: resourceId, type: resourceType, zoneType },
    });

  return (
    <div
      ref={connect}
      className={[
        'rounded-lg bg-white p-3 border transition-colors relative',
        isDragging ? 'opacity-50' : '',
        isHighlighted ? 'border-2 border-dashed border-teal-500' : 'border-gray-200',
      ].join(' ')}
    >
      {isHighlighted && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] bg-teal-500 text-white px-2 py-0.5 rounded-full whitespace-nowrap">
          drop to group
        </div>
      )}
      <button
        type="button"
        onClick={handleRemove}
        aria-label={`Remove ${leaf.name || resourceType}`}
        title={`Remove ${leaf.name || resourceType}`}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-base leading-none px-1.5 py-0.5 rounded hover:bg-gray-100"
      >
        ×
      </button>
      <div className="text-xs text-gray-500 mb-1 truncate uppercase tracking-wide pr-6">
        {label}
      </div>
      <div className="flex flex-row items-center w-full pr-6">
        <span className="font-medium text-gray-900">{leaf.name || resourceId}</span>
      </div>
    </div>
  );
}
